import { createGqlFetcher } from 'app/lib/gqlFetcher'
import { ArrowPathIcon } from '@heroicons/react/20/solid'
import { withZod } from '@remix-validated-form/with-zod'
import classNames from 'classnames'
import { isEmpty, omitBy } from 'lodash'
import { useEffect, useState } from 'react'
import { z } from 'zod'
import { AddNewIntegrationModal } from '../AddNewIntegrationModal'
import { SAVE_FACEBOOK_ADS_INTEGRATION, REMOVE_LOCATION_INTEGRATION } from '../ops-graphql'
import Button from 'app/components/Button'
import { useConfirmation } from 'app/components/ConfirmationContext'
import FormCombo from 'app/components/form/FormCombo'
import FormComboAsync from 'app/components/form/FormComboAsync'
import FormComboMultiSelect from 'app/components/form/FormComboMultiSelect'
import FormSelect from 'app/components/form/FormComboSelect'
import { SmartForm } from 'app/components/form/SmartForm'
import Spinner from 'app/components/Spinner'
import type { LocationJson, LocationType } from 'app/types/entities'
import { closeModal, showModal } from 'app/lib/modal'
import { gqlOpsMutation } from 'app/lib/opsGraphqlClient'
import { notifyError, notifySuccess } from 'app/services/notify.client'
import { bg } from 'lib/background'
import { debug, logError } from 'lib/logging'
import { OptionType } from 'types/OptionType'
import type { VariablesOf } from '@graphql-typed-document-node/core'

const createSchema = z.object({
  locationId: z.string().min(1, { message: `A Location is required` }),
  account: z.string().min(1, { message: `An Account is required` }),
  campaigns: z
    .array(
      z.object({
        id: z.string(),
        meta: z.object({ type: z.string(), parentId: z.string().optional(), grandparentId: z.string().optional() }),
      }),
    )
    .transform((val) =>
      val
        .filter((v) => v.id !== `0`) // Filter out deselectAllOptions
        .map((v) => ({ id: v.id, type: v.meta.type, parentId: v.meta.parentId, grandparentId: v.meta.grandparentId })),
    )
    .optional(),
})

export const createFacebookAdsValidator = withZod(createSchema)

export function FacebookAdsFormModal({
  onAdd,
  currentValues,
}: {
  onAdd: () => void
  currentValues?: { locationId: string; locationName: string; facebookAds: LocationType[`facebookAdsV2`] }
}) {
  const [selectedLocation, setSelectedLocation] = useState<OptionType | undefined>()
  const [accountsResponse, setAccountsResponse] = useState<{
    data?: {
      account: {
        id: string | undefined
        name: string | null | undefined
        isUsed: boolean
        isDisabled: boolean
      }
    }[]
    error?: any
  }>()
  const [campaignsResponse, setCampaignsResponse] = useState<{
    data?: {
      campaigns: {
        id: string | null | undefined
        name?: string | null
        isUsed: boolean
        isDisabled: boolean
        adSets?: {
          id: string | null | undefined
          name?: string | null
          isUsed: boolean
        }[]
      }[]
    }
    error?: any
  }>()
  const [accountOptions, setAccountOptions] = useState<OptionType[]>([])
  const [accountsLoading, setAccountsLoading] = useState(false)
  const [accountsError, setAccountsError] = useState(false)
  const [campaignOptions, setCampaignOptions] = useState<OptionType[]>()
  const [selectedAccountOption, setSelectedAccountOption] = useState<OptionType | undefined>(undefined)
  const [selectedCampaignOptions, setSelectedCampaignOptions] = useState<OptionType[]>([])
  const [confirm] = useConfirmation()
  const [isSaving, setIsSaving] = useState(false)

  const fetchAccountOptions = (refresh?: boolean) => {
    const composedSearchParams = new URLSearchParams(
      omitBy({ refresh: refresh ? `true` : ``, locationId: currentValues?.locationId ?? `` }, isEmpty),
    )
    const url = `/api/integrations/facebook/accounts?${composedSearchParams.toString()}`

    setAccountsResponse(undefined)
    setAccountOptions([])
    setAccountsError(false)
    setAccountsLoading(true)
    bg(async () => {
      try {
        const res = await window.fetch(url, { credentials: `include` })
        if (!res.ok) throw new Error(`HTTP ${res.status}`)
        const response = await res.json()
        debug(`fb`, `response`, response)
        if (response.error) {
          setAccountsError(true)
        } else {
          setAccountsResponse(response)
        }
      } catch (err) {
        logError(`Failed to load Facebook Ads account options:`, err)
        setAccountsError(true)
      } finally {
        setAccountsLoading(false)
      }
    })
  }

  const fetchCampaignOptions = (refresh?: boolean) => {
    const composedSearchParams = new URLSearchParams(
      omitBy({ refresh: refresh ? `true` : ``, locationId: currentValues?.locationId ?? `` }, isEmpty),
    )
    const url = `/api/integrations/facebook/accounts/${selectedAccountOption?.id}?${composedSearchParams.toString()}`

    setCampaignsResponse(undefined)
    setCampaignOptions([])
    if (url) {
      bg(async () => {
        let response = await window.fetch(url, { credentials: `include` }).then((r) => r.json())
        debug(`fb`, `response`, response)
        if (!response.error) {
          setCampaignsResponse(response)
        }
      })
    }
  }

  const onRefreshClick = () => {
    fetchCampaignOptions(true)
  }

  const onSave = async () => {
    setIsSaving(true)
    const locationId = currentValues?.locationId || (selectedLocation?.value as string)
    if (!locationId) {
      setIsSaving(false)
      return
    }

    const campaigns = selectedCampaignOptions.map((opt) => ({
      id: opt.id,
      type: opt.meta?.type || `campaign`,
      parentId: opt.meta?.parentId || selectedAccountOption?.id,
      grandparentId: opt.meta?.grandparentId,
    }))

    const variables: VariablesOf<typeof SAVE_FACEBOOK_ADS_INTEGRATION> = {
      locationId,
      account: selectedAccountOption?.id,
      campaigns: campaigns.length ? campaigns : undefined,
      isNew: !currentValues,
    }
    const result = await gqlOpsMutation(SAVE_FACEBOOK_ADS_INTEGRATION, variables)
    setIsSaving(false)
    const data = result.data?.saveFacebookAdsIntegration
    if (data?.success) {
      notifySuccess(`Facebook Ads integration saved`)
      onAdd()
      closeModal()
    } else {
      notifyError(`Failed to save integration: ${data?.error || result.error || 'Unknown error'}`)
    }
  }

  const onSubmit = () => {
    onSave()
  }

  const goBackToSelectIntegrationModal = () => {
    closeModal()
    showModal(
      <AddNewIntegrationModal onIntegrationAdded={onAdd} />,
      {
        title: `Add New Integration`,
        isFullScreen: false,
        overflowHidden: false,
        removePadding: true,
        useRadix: true,
      },
      false,
    )
  }

  // Fetch options on mount
  useEffect(() => {
    fetchAccountOptions()
  }, [])

  // Fetch campaign options when account option is selected
  useEffect(() => {
    if (selectedAccountOption) {
      fetchCampaignOptions()
    }
  }, [selectedAccountOption])

  useEffect(() => {
    if (accountsResponse && accountsResponse.data) {
      const responseOptions = accountsResponse.data.map((hit: any) => ({
        id: hit.account.id || hit.account.name,
        name: hit.account.name,
        value: hit.account.id,
        meta: {
          isUsed: hit.account.isUsed,
          isDisabled: hit.account.isDisabled,
          tooltipForDisabledOption: `This account is connected to another location`,
          type: `account`,
        },
      }))
      setAccountOptions(responseOptions)
      if (currentValues?.facebookAds?.length) {
        ;(currentValues.facebookAds as any[]).forEach(({ accountId, campaignId, adSetId }) => {
          if (accountId) {
            const account = responseOptions.find((option) => option.id === accountId)
            if (account) {
              setSelectedAccountOption(account as OptionType)
            }
          }
        })
      }
    }
  }, [accountsResponse])

  useEffect(() => {
    if (campaignsResponse && campaignsResponse.data) {
      const responseOptions = campaignsResponse.data.campaigns.map((campaign: any) => ({
        id: campaign.id,
        name: campaign.name,
        value: campaign.id,
        meta: {
          isUsed: campaign.isUsed,
          isDisabled: campaign.isDisabled,
          tooltipForDisabledOption: `This campaign is connected to another location`,
          type: `campaign`,
          parentId: selectedAccountOption!.id,
        },
        children: campaign.adSets?.map((adSet: any) => ({
          id: adSet.id,
          name: adSet.name,
          value: adSet.id,
          meta: {
            isUsed: adSet.isUsed,
            tooltipForDisabledOption: `This ad set is connected to another location`,
            type: `adSet`,
            parentId: campaign.id,
            grandparentId: selectedAccountOption!.id,
          },
        })),
      }))
      setCampaignOptions(responseOptions)
      if (currentValues?.facebookAds?.length) {
        const selection: OptionType[] = []
        ;(currentValues.facebookAds as any[]).forEach(({ accountId, campaignId, adSetId }) => {
          if (accountId && campaignId && !adSetId) {
            const campaign = responseOptions.find(
              (option) => option.id === campaignId && option.meta.parentId === accountId,
            )
            if (campaign) {
              selection.push(campaign)
            }
          }
          if (accountId && campaignId && adSetId) {
            const campaign = responseOptions.find(
              (option) => option.id === campaignId && option.meta.parentId === accountId,
            )
            if (campaign) {
              selection.push(campaign.children.find((option) => option.id === adSetId) as OptionType)
            }
          }
        })
        setSelectedCampaignOptions(selection)
      }
    }
  }, [campaignsResponse])

  const onRemove = () => {
    return confirm({
      title: `Remove Facebook Ads Integration`,
      description: `Removing this integration will also remove all associated data. Do you want to continue?`,
      variant: `danger`,
    }).then(() =>
      gqlOpsMutation(REMOVE_LOCATION_INTEGRATION, {
        locationId: currentValues?.locationId!,
        integration: `facebook`,
      }).then((result) => {
        if (result.data?.removeLocationIntegration?.success) {
          notifySuccess(`Facebook Ads integration removed`)
          onAdd()
          closeModal()
        } else {
          notifyError(`Failed to remove integration: ${result.error || 'Unknown error'}`)
        }
      }),
    )
  }

  return (
    <>
      <SmartForm
        id="integration-form"
        className="!mt-0 w-[600px] grow-0 justify-start !gap-y-4 px-6 py-4"
        validator={createFacebookAdsValidator}
        reloadDocument={false}
        preventSubmit={true}
        onSubmit={onSubmit}
        hideButtons={true}
        method={currentValues ? `PUT` : `POST`}
        hiddenInputs={currentValues ? [{ name: `locationId`, value: currentValues.locationId }] : undefined}
      >
        {currentValues ? (
          <FormSelect
            cols={5}
            name="locationId"
            label="Trackable Location"
            options={[{ label: currentValues.locationName, value: currentValues.locationId }]}
            value={currentValues.locationId}
            disabled
            labelClassName="text-body-sm text-strong"
          />
        ) : (
          <FormComboAsync
            cols={5}
            name="locationId"
            required={true}
            showSearchInDropdown={true}
            label="Trackable Location"
            value={selectedLocation}
            optionsMapper={(res) =>
              res?.hits?.map((hit: LocationJson) => ({
                id: hit.id || hit.name,
                name: hit.name,
                value: hit.id,
                meta: {
                  isUsed: !!hit.facebookAdsV2,
                  tooltipForDisabledOption: `This location is already connected to Facebook Ads`,
                },
              }))
            }
            fetcher={createGqlFetcher('locations')}
            labelClassName="text-body-sm text-strong"
            className="text-body-sm"
            onChange={(val) => setSelectedLocation(val)}
            newLineError={false}
            showErrorIcon
          />
        )}
        {accountOptions.length ? (
          <FormCombo
            cols={5}
            name="account"
            label="Account"
            value={selectedAccountOption}
            options={accountOptions}
            labelClassName="text-body-sm text-strong"
            className="text-body-sm"
            onChange={(val) => {
              setSelectedAccountOption(val)
            }}
            // optionsContainerClassName="!w-[380px]"
            newLineError={false}
            showErrorIcon
          />
        ) : accountsLoading ? (
          <div className="col-span-5 flex items-center pt-5">
            <Spinner size={5} />
          </div>
        ) : accountsError ? (
          <div className="col-span-5 pt-5 text-sm text-red-600">Failed to load accounts</div>
        ) : (
          <div className="col-span-5 pt-5 text-sm text-subtle">No accounts available</div>
        )}
        {!accountOptions.length || !selectedAccountOption ? null : campaignOptions != null ? (
          <>
            <FormComboMultiSelect
              cols={5}
              name="campaigns"
              label="Campaigns"
              values={selectedCampaignOptions}
              deselectAllOptions={[{ name: `All`, id: `0`, value: ``, meta: { type: `deselectAll` } }]}
              enableDeselectAllOption={true}
              options={campaignOptions}
              labelClassName="text-body-sm text-strong"
              className="text-body-sm"
              onChange={(val) => {
                setSelectedCampaignOptions(val)
              }}
              placeholder="All"
              // optionsContainerClassName="!w-[380px]"
              newLineError={false}
              checkboxSide="left"
              showErrorIcon
            />
            <div className="col-span-1 flex items-center justify-center pt-5">
              <ArrowPathIcon
                className={classNames(`ml-2 h-5 w-5 cursor-pointer`, !campaignsResponse && `animate-spin`)}
                onClick={() => onRefreshClick()}
              />
            </div>
          </>
        ) : (
          <div className="col-span-5 flex items-center pt-5">
            <Spinner size={5} />
          </div>
        )}
      </SmartForm>
      <div className="sticky bottom-0 w-full rounded-b-xl border-t border-default bg-surface-raised px-6 py-3">
        <div className="flex items-center justify-between">
          <div>
            {currentValues ? <Button label="Remove" mode="danger" size="md" onClick={() => onRemove()} /> : null}
          </div>
          <div className="flex gap-2">
            {!currentValues ? (
              <Button label="Back" mode="white" size="md" onClick={() => goBackToSelectIntegrationModal()} />
            ) : (
              <Button label="Cancel" mode="white" size="md" onClick={() => closeModal()} />
            )}

            <Button
              type="submit"
              form="integration-form"
              label="Save"
              mode="primary"
              size="md"
              disabled={(!selectedLocation && !currentValues?.locationId) || !selectedAccountOption}
              // onClick={() => setSaving(true)}
              loading={isSaving}
            />
          </div>
        </div>
      </div>
    </>
  )
}
